import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCategories, useColors, useSizeGuides, useMaterials, useCollections, useOccasions } from '../../../features/products/hooks/useMetaData'
import { productsApi } from '../../../features/products/api/productsApi'
import type { PropertyData, FormData as ProductFormData } from '../types'
import { transformToApiFormat } from '../utils/dataTransform'
import { isSaleModeValid } from '../utils/saleMode'
import { UnresolvedMetadataError } from '../../../features/products/utils/productPayload'
import { useToast } from '../../../shared/components/toast/useToast'
import { DEFAULT_STOCK_UP_TRIGGER } from '../../../features/products/utils/stockStatus'

export const useAddProduct = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: categoriesData } = useCategories()
  const { data: colorsData } = useColors()
  const { data: sizeGuideData } = useSizeGuides()
  const { data: materialsData } = useMaterials()
  // PE-2087 — many-to-many taxonomy option sources (product-service id namespace).
  const { data: collectionsData } = useCollections()
  const { data: occasionsData } = useOccasions()

  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    category: '',
    details: '',
    price: '',
    baseCostPrice: '',
    productionTimeInDays: '',
    styleGuide: '',
    quantity: 0,
    images: [],
    discountPercentage: 0,
    media: [],
    saleMode: 'PRICE',
    bookingServiceId: '',
    stockUpTrigger: String(DEFAULT_STOCK_UP_TRIGGER),
    collectionIds: [],
    categoryIds: [],
    occasionIds: [],
  })

  const [propertyData, setPropertyData] = useState<PropertyData>({
    sizes: []
  })

  const [selectedSize, setSelectedSize] = useState('')
  const [selectedMaterial, setSelectedMaterial] = useState('')
  const [selectedColor, setSelectedColor] = useState('')
  const [showPropertiesModal, setShowPropertiesModal] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)


  // Validation
  // PE-2099 — the pricing half of the check is sale-mode dependent: a
  // CONSULTATION_REQUIRED product has no price and needs a booking service instead.
  const isBaseDataValid = () => {
    return !!(formData.name && formData.category && formData.details) && isSaleModeValid(formData)
  }

  const canAddVariants = () => {
    return isBaseDataValid()
  }

  // Load/Save localStorage
  useEffect(() => {
    const saved = localStorage.getItem('addProductData')
    if (saved) {
      const data = JSON.parse(saved)
      // PE-2099 — drafts persisted before the sale-mode toggle have neither field.
      // PE-2087 — drafts persisted before the taxonomy pickers have none of the three arrays.
      const restored = data.formData || formData
      setFormData({
        saleMode: 'PRICE',
        bookingServiceId: '',
        stockUpTrigger: String(DEFAULT_STOCK_UP_TRIGGER),
        ...restored,
        collectionIds: restored.collectionIds ?? [],
        categoryIds: restored.categoryIds ?? [],
        occasionIds: restored.occasionIds ?? [],
      })
      setPropertyData(data.propertyData || { sizes: [] })
      setShowPropertiesModal(data.showPropertiesModal || false)
      setCurrentStep(data.currentStep || 0)
      setSelectedSize(data.selectedSize || '')
      setSelectedMaterial(data.selectedMaterial || '')
      setSelectedColor(data.selectedColor || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const dataToSave = {
      formData, propertyData, showPropertiesModal, currentStep,
      selectedSize, selectedMaterial, selectedColor
    }
    localStorage.setItem('addProductData', JSON.stringify(dataToSave))
  }, [formData, propertyData, showPropertiesModal, currentStep, selectedSize, selectedMaterial, selectedColor])

  // Mutations
  const createDraftMutation = useMutation({
    mutationFn: (data: FormData) => productsApi.drafts.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['products', 'drafts'] })
      localStorage.removeItem('addProductData')
      navigate('/products')
    }
  })

  const createProductMutation = useMutation({
    mutationFn: (data: FormData) => productsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['products', 'drafts'] })
      localStorage.removeItem('addProductData')
      navigate('/products')
    }
  })

  // Actions
  // PE-2220 — the transform now throws UnresolvedMetadataError instead of posting
  // a `"string"` sentinel that rack-api rejects as a non-UUID. Surface the named
  // field to the merchant rather than letting it reach an error boundary.
  const buildPayload = (status: 'DRAFT' | 'PUBLISHED') => {
    try {
      return transformToApiFormat(formData, propertyData, {
        categoriesData, colorsData, sizeGuideData, materialsData
      }, status)
    } catch (err) {
      if (err instanceof UnresolvedMetadataError) {
        toast.error(err.message)
        return null
      }
      throw err
    }
  }

  const handleSaveDraft = async () => {
    if (!isBaseDataValid()) return

    const transformedData = buildPayload('DRAFT')
    if (!transformedData) return
    createDraftMutation.mutate(transformedData)
  }

  const handlePublish = async () => {
    if (!isBaseDataValid()) return

    const transformedData = buildPayload('PUBLISHED')
    if (!transformedData) return
    createProductMutation.mutate(transformedData)
  }

  return {
    // Data
    formData, setFormData,
    propertyData, setPropertyData,
    selectedSize, setSelectedSize,
    selectedMaterial, setSelectedMaterial,
    selectedColor, setSelectedColor,
    showPropertiesModal, setShowPropertiesModal,
    currentStep, setCurrentStep,
    
    // API Data
    categories: categoriesData?.data || [],
    apiColors: colorsData?.data || [],
    sizeGuides: sizeGuideData?.result?.data || [],
    materials: materialsData?.data || [],
    collections: collectionsData?.data || [],
    occasions: occasionsData?.data || [],
    
    // Validation
    isBaseDataValid,
    canAddVariants,
    
    // Loading
    isDraftLoading: createDraftMutation.isPending,
    isPublishLoading: createProductMutation.isPending,
    
    // Actions
    handleSaveDraft,
    handlePublish,
    navigate
  }
}